// src/components/PullUsersModal.tsx

import React, { useState } from 'react';
import {
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Button,
    Alert,
    Spinner,
    Progress,
    Table,
} from 'reactstrap';
import { Download, Users, CheckCircle, XCircle, AlertCircle } from 'react-feather';
import { pullUsersFromDevice } from '../api/employee';

interface PullUsersModalProps {
    isOpen: boolean;
    toggle: () => void;
    onSuccess?: () => void;
}

const PullUsersModal: React.FC<PullUsersModalProps> = ({
    isOpen,
    toggle,
    onSuccess,
}) => {
    const [loading, setLoading] = useState(false);
    const [progress, setProgress] = useState(0);
    const [result, setResult] = useState<any | null>(null);
    const [error, setError] = useState<string | null>(null);

    const handlePullUsers = async () => {
        setLoading(true);
        setProgress(0);
        setError(null);
        setResult(null);

        // Simulate progress
        const progressInterval = setInterval(() => {
            setProgress((prev) => {
                if (prev >= 90) {
                    clearInterval(progressInterval);
                    return 90;
                }
                return prev + 10;
            });
        }, 300);

        try {
            const response = await pullUsersFromDevice();

            clearInterval(progressInterval);
            setProgress(100);
            setResult(response);

            if (response.success && response.data.inserted > 0) {
                // Call onSuccess callback if provided
                if (onSuccess) {
                    setTimeout(() => {
                        onSuccess();
                    }, 1000);
                }
            }
        } catch (error: any) {
            clearInterval(progressInterval);
            setError(error.response?.data?.message || error.message || 'Failed to pull users from device');
            setProgress(0);
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        if (!loading) {
            toggle();
            setResult(null);
            setError(null);
            setProgress(0);
        }
    };

    return (
        <Modal isOpen={isOpen} toggle={handleClose} size="lg" backdrop="static" keyboard={!loading}>
            <ModalHeader toggle={handleClose}>
                <div className="d-flex align-items-center">
                    <Download size={18} className="me-2" />
                    <span>Pull Users from Device</span>
                </div>
            </ModalHeader>

            <ModalBody>
                {error && (
                    <Alert color="danger" className="mb-3">
                        <XCircle size={16} className="me-2" />
                        {error}
                    </Alert>
                )}

                {result ? (
                    // Success/Result View
                    <div>
                        <Alert color={result.data.inserted > 0 ? 'success' : 'info'}>
                            <div className="d-flex align-items-start">
                                {result.data.inserted > 0 ? (
                                    <CheckCircle size={20} className="me-2 flex-shrink-0 mt-1" />
                                ) : (
                                    <AlertCircle size={20} className="me-2 flex-shrink-0 mt-1" />
                                )}
                                <div>
                                    <strong>{result.message}</strong>
                                    <div className="mt-2">
                                        <div className="d-flex gap-3 flex-wrap">
                                            <div className="p-2 bg-success bg-opacity-10 rounded text-center">
                                                <div className="fw-bold text-success">{result.data.inserted}</div>
                                                <small className="text-muted">New Employees</small>
                                            </div>
                                            <div className="p-2 bg-info bg-opacity-10 rounded text-center">
                                                <div className="fw-bold text-info">{result.data.updated}</div>
                                                <small className="text-muted">Updated</small>
                                            </div>
                                            <div className="p-2 bg-secondary bg-opacity-10 rounded text-center">
                                                <div className="fw-bold text-secondary">{result.data.total}</div>
                                                <small className="text-muted">Total Synced</small>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </Alert>

                        {result.data.employees && result.data.employees.length > 0 && (
                            <div className="mt-3">
                                <h6 className="mb-2">Synced Employees:</h6>
                                <div className="table-responsive" style={{ maxHeight: '200px', overflow: 'auto' }}>
                                    <Table size="sm" bordered className="mb-0">
                                        <thead className="table-light">
                                            <tr>
                                                <th>#</th>
                                                <th>Employee Code</th>
                                                <th>Name</th>
                                                <th>Device</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {result.data.employees.map((emp: { id: any; employee_code: string | number | bigint | boolean | React.ReactElement<unknown, string | React.JSXElementConstructor<any>> | Iterable<React.ReactNode> | React.ReactPortal | Promise<string | number | bigint | boolean | React.ReactPortal | React.ReactElement<unknown, string | React.JSXElementConstructor<any>> | Iterable<React.ReactNode> | null | undefined> | Iterable<React.ReactNode> | null | undefined; name: string | number | bigint | boolean | React.ReactElement<unknown, string | React.JSXElementConstructor<any>> | Iterable<React.ReactNode> | React.ReactPortal | Promise<string | number | bigint | boolean | React.ReactPortal | React.ReactElement<unknown, string | React.JSXElementConstructor<any>> | Iterable<React.ReactNode> | null | undefined> | Iterable<React.ReactNode> | null | undefined; device_serial: any; }, index: number) => (
                                                <tr key={emp.id || index}>
                                                    <td>{index + 1}</td>
                                                    <td>{emp.employee_code}</td>
                                                    <td>{emp.name}</td>
                                                    <td>{emp.device_serial || 'N/A'}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                            </div>
                        )}
                    </div>
                ) : (
                    // Initial/Loading View
                    <div>
                        <Alert color="info" className="mb-3">
                            <Users size={16} className="me-2" />
                            This will fetch all users from the attendance device and sync them with the system.
                        </Alert>

                        <div className="text-center py-3">
                            <div className="mb-3">
                                <Download size={48} className="text-primary" />
                            </div>
                            <h6>Ready to pull users from device</h6>
                            <p className="text-muted small">
                                This will import all users from the attendance device and add them to the system.
                            </p>
                        </div>

                        {loading && (
                            <div className="mt-3">
                                <div className="d-flex justify-content-between mb-1">
                                    <span className="text-muted small">Syncing users...</span>
                                    <span className="text-muted small">{progress}%</span>
                                </div>
                                <Progress value={progress} color="primary" animated />
                                <p className="text-muted small text-center mt-2">
                                    Please wait while we sync users from the device...
                                </p>
                            </div>
                        )}
                    </div>
                )}
            </ModalBody>

            <ModalFooter>
                {result ? (
                    <Button color="secondary" onClick={handleClose}>
                        Close
                    </Button>
                ) : (
                    <>
                        <Button color="light" onClick={handleClose} disabled={loading}>
                            Cancel
                        </Button>
                        <Button
                            color="primary"
                            onClick={handlePullUsers}
                            disabled={loading}
                        >
                            {loading ? (
                                <>
                                    <Spinner size="sm" className="me-2" />
                                    Pulling Users...
                                </>
                            ) : (
                                <>
                                    <Download size={14} className="me-1" />
                                    Pull Users
                                </>
                            )}
                        </Button>
                    </>
                )}
            </ModalFooter>
        </Modal>
    );
};

export default PullUsersModal;